import { ApiKey } from '@pubstack/common/src/site'
import { sortByName } from '@pubstack/common/src/sort'
import { canAccess, isPubstackUserRole } from '@pubstack/common/src/rolesAndFeatures'
import { useScopes } from '@pubstack/http/useScopes'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { MutationToasts, useMutation, useQuery } from '~/api/api-client'

export const API_KEYS_QUERY_KEYS = ['settings', 'api-keys']
const url = `/api-keys`
//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useApiKeys = () => {
  return useQuery<ApiKey[]>({
    url,
    keys: API_KEYS_QUERY_KEYS,
    select: sortByName,
  })
}
//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateApiKeys = (toasts: MutationToasts = {}) => {
  return useMutation<Omit<ApiKey, 'id' | 'scopeId'>>({
    method: 'post',
    url,
    collectionKeys: API_KEYS_QUERY_KEYS,
    ...toasts,
  })
}

//-------------------------------------------------------------------------
// Service API keys (hashed, multi-scope). Owner only: the backend answers 403 to anyone else.
//-------------------------------------------------------------------------
// Mirrors `HashedApiKeyView` / `CreatedHashedApiKey` (backend/src/domain/hashedApiKeys.ts) and `CreateServiceApiKeySchema`
// (backend/src/api/app/service-api-key-resource.ts): the backend domain is not importable from the frontend.
export type ServiceApiKeyStatus = 'active' | 'expired' | 'revoked'

export type ServiceApiKey = {
  id: string
  name: string
  /** Only the scopes the current user can see */
  scopes: { id: string; name: string }[]
  /** Scopes of the key the current user does not own, hence not listed in `scopes` */
  otherScopesCount: number
  permissions: 'read'[]
  prefix: 'psk_'
  last4: string
  status: ServiceApiKeyStatus
  createdAt: string
  createdBy: string
  expiresAt: string | null
  lastUsedAt: string | null
  revokedAt: string | null
  revokedBy: string | null
}

export type CreateServiceApiKey = {
  name: string
  scopeIds: string[]
  /** ISO 8601 datetime with offset */
  expiresAt?: string
}

/** The plaintext `key` is only returned by the creation call */
export type CreatedServiceApiKey = {
  key: string
  apiKey: ServiceApiKey
}

export const SERVICE_API_KEYS_QUERY_KEYS = ['settings', 'service-api-keys']
const serviceUrl = `/service-api-keys`

/**
 * Enabled scopes the current user may attach a service key to, like the backend check: owner on each, and the
 * serviceApiKeys flag on each (read from the scope, the token only knows the current one). Pubstack staff are owner
 * everywhere and skip the flag; in view mode, useAuthenticatedUser already gives them the role they view.
 */
export const useOwnerScopes = () => {
  const { userRoles } = useAuthenticatedUser()
  const { data, isLoading } = useScopes()
  const staffBypass = isPubstackUserRole(userRoles)
  return {
    ownerScopes: (data ?? []).filter((scope) => scope.enabled !== false && canAccess(scope.id, userRoles, 'owner') && (staffBypass || (scope.accessFeatures ?? []).includes('serviceApiKeys'))),
    isLoading,
  }
}

export const useServiceApiKeys = () => {
  return useQuery<ServiceApiKey[]>({
    url: serviceUrl,
    keys: SERVICE_API_KEYS_QUERY_KEYS,
  })
}

export const useCreateServiceApiKey = (toasts: MutationToasts = {}) => {
  return useMutation<CreateServiceApiKey, CreatedServiceApiKey>({
    method: 'post',
    url: serviceUrl,
    collectionKeys: SERVICE_API_KEYS_QUERY_KEYS,
    // the response holds the plaintext key: drop it from the mutation cache as soon as the form is gone
    gcTime: 0,
    ...toasts,
  })
}

export const useRevokeServiceApiKey = (toasts: MutationToasts = {}) => {
  return useMutation<{ id: string }, ServiceApiKey>({
    method: 'post',
    url: ({ id }) => `${serviceUrl}/${encodeURIComponent(id)}/revoke`,
    collectionKeys: SERVICE_API_KEYS_QUERY_KEYS,
    ...toasts,
  })
}
